import { useState, useEffect, useMemo } from 'react';
import { Loader2, Printer } from 'lucide-react';
import { useSemData, type DateRangeKey, type CustomDateRange } from '@/hooks/useSemData';
import { useSyncStatus } from '@/hooks/useSyncStatus';
import type { SemData, RsaAssetSummary } from '@/types/sem';
import type { LandingPageReport } from '@/types/landing-page-analysis';
import {
  PortfolioPulse,
  PortfolioScore,
  PropertyBreakdown,
  FunnelTeaser,
  LandingPageDiagnosis,
  LiveIndicator,
  DateRangeSelector,
  TrendsTab,
  FullFunnelTab,
  RestaurantCampaignCards,
  LandingPageStory,
  AdGroupComparison,
  RestaurantPillTabs,
} from '@/components/sem';

type TabId = 'overview' | 'campaigns' | 'trends' | 'full-funnel' | 'landing-pages' | 'compare';

const TABS: { id: TabId; label: string; badge?: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'campaigns', label: 'Campaigns' },
  { id: 'trends', label: 'Trends', badge: 'New' },
  { id: 'full-funnel', label: 'Full Funnel' },
  { id: 'landing-pages', label: 'Landing Pages' },
  { id: 'compare', label: 'Compare' },
];

export function SemReport() {
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [dateRange, setDateRange] = useState<DateRangeKey>('30d');
  const [customRange, setCustomRange] = useState<CustomDateRange | undefined>(undefined);
  const [_autoOpenRestaurant, setAutoOpenRestaurant] = useState<string | null>(null);
  const [selectedRestaurantId, setSelectedRestaurantId] = useState<string | null>(null);

  // Supabase data (falls back to static JSON if Supabase is empty/unconfigured)
  const { restaurants, dailySnapshots, prevSnapshots, aggregatedSummary, dateRange: dateRangeDates, loading, error, dataSource } = useSemData(dateRange, customRange);
  const syncStatus = useSyncStatus();
  const isLive = dataSource === 'supabase';

  // Landing page report (still loaded from static JSON for now)
  const [lpReport, setLpReport] = useState<LandingPageReport | null>(null);
  useEffect(() => {
    fetch('/data/landing-page-report.json')
      .then(res => res.ok ? res.json() : null)
      .then(data => setLpReport(data))
      .catch(() => null);
  }, []);

  // Initialize selected restaurant from deep-link or default to first
  useEffect(() => {
    if (!lpReport || restaurants.length === 0) return;
    if (selectedRestaurantId) return;
    if (_autoOpenRestaurant) {
      const match = restaurants.find(r => r.name === _autoOpenRestaurant);
      if (match) { setSelectedRestaurantId(match.id); return; }
    }
    setSelectedRestaurantId(restaurants[0].id);
  }, [lpReport, restaurants, _autoOpenRestaurant, selectedRestaurantId]);

  // Build combined SemData for backward-compat tabs
  const combinedSemData = useMemo((): SemData => {
    const allCampaigns = restaurants.flatMap(r => r.data.campaigns);

    // Aggregate RSA performance
    const headlineMap = new Map<string, RsaAssetSummary>();
    const descriptionMap = new Map<string, RsaAssetSummary>();
    let totalAds = 0;
    for (const r of restaurants) {
      totalAds += r.data.rsaPerformance.totalAds;
      for (const h of r.data.rsaPerformance.headlines) {
        const existing = headlineMap.get(h.text);
        if (existing) { existing.impressions += h.impressions; existing.adsUsedIn += h.adsUsedIn; }
        else { headlineMap.set(h.text, { ...h }); }
      }
      for (const d of r.data.rsaPerformance.descriptions) {
        const existing = descriptionMap.get(d.text);
        if (existing) { existing.impressions += d.impressions; existing.adsUsedIn += d.adsUsedIn; }
        else { descriptionMap.set(d.text, { ...d }); }
      }
    }

    return {
      meta: {
        accountName: 'All Restaurants',
        accountId: 'combined',
        dateRange: restaurants[0]?.data.meta.dateRange || { start: '', end: '' },
        currency: 'USD',
        generatedAt: new Date().toISOString(),
      },
      summary: aggregatedSummary,
      campaigns: allCampaigns,
      rsaPerformance: {
        totalAds,
        headlines: Array.from(headlineMap.values()).sort((a, b) => b.impressions - a.impressions),
        descriptions: Array.from(descriptionMap.values()).sort((a, b) => b.impressions - a.impressions),
      },
      keywords: [],
      recommendations: restaurants.flatMap(r => r.data.recommendations || []),
    };
  }, [restaurants, aggregatedSummary]);

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto py-20 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || restaurants.length === 0) {
    return (
      <div className="max-w-6xl mx-auto py-20 text-center">
        <p className="text-muted-foreground">Failed to load SEM data: {error || 'No data found'}</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-6 sm:space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-display font-light tracking-tight">SRC Restaurant Group</h1>
          <p className="text-[var(--ed-t3)] mt-1 text-sm">
            Google Ads Performance Dashboard
            {isLive && dateRangeDates && (
              <span className="text-muted-foreground/50">
                {' '}· {new Date(dateRangeDates.start + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – {new Date(dateRangeDates.end + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
            )}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3 sm:gap-4">
          {isLive ? (
            <>
              <LiveIndicator syncStatus={syncStatus} />
              <DateRangeSelector value={dateRange} onChange={setDateRange} customRange={customRange} onCustomRangeChange={setCustomRange} />
              <button
                onClick={() => window.print()}
                className="no-print flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[var(--ed-rule)] bg-[var(--ed-bg-2)] text-[0.7rem] text-[var(--ed-t2)] hover:text-[var(--ed-t1)] hover:border-[var(--ed-rule-heavy)] transition-colors"
                title="Print or save as PDF"
              >
                <Printer className="h-3.5 w-3.5" />
                Export
              </button>
            </>
          ) : (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[var(--ed-amber-bg)] border border-[rgba(224,168,82,0.15)]">
              <div className="w-1.5 h-1.5 rounded-full bg-[var(--ed-amber)]" />
              <span className="text-[0.7rem] text-[var(--ed-amber)]">Static Data</span>
              <span className="text-[0.65rem] text-muted-foreground/50">
                {restaurants[0]?.data.meta.dateRange.start} – {restaurants[0]?.data.meta.dateRange.end}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="">
        <nav className="flex gap-4 sm:gap-6 overflow-x-auto">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-3 text-sm font-medium transition-colors relative flex items-center gap-1.5 ${
                activeTab === tab.id
                  ? 'text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.label}
              {tab.badge && (
                <span className={`text-[0.55rem] px-1.5 py-0.5 rounded-full ${
                  tab.badge === 'New'
                    ? 'bg-[var(--ed-green-bg)] text-[var(--ed-green)] border border-[rgba(74,186,122,0.12)]'
                    : 'bg-[var(--ed-amber-bg)] text-[var(--ed-amber)] border border-[rgba(224,168,82,0.12)]'
                }`}>
                  {tab.badge}
                </span>
              )}
              {activeTab === tab.id && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-foreground" />
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-10">
          {isLive && (
            <PortfolioScore
              summary={aggregatedSummary}
              restaurants={restaurants}
              lpReport={lpReport}
            />
          )}
          {isLive && (
            <PortfolioPulse
              summary={aggregatedSummary}
              dailySnapshots={dailySnapshots}
              prevSnapshots={prevSnapshots}
            />
          )}
          {isLive && (
            <PropertyBreakdown
              restaurants={restaurants}
              dailySnapshots={dailySnapshots}
              prevSnapshots={prevSnapshots}
            />
          )}
          {!isLive && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
              {[
                { label: 'Total Spend', value: `$${Math.round(aggregatedSummary.spend).toLocaleString()}` },
                { label: 'Conversions', value: Math.round(aggregatedSummary.conversions).toLocaleString() },
                { label: 'ROAS', value: `${aggregatedSummary.roas.toFixed(2)}x`, color: 'text-[var(--ed-green)]' },
                { label: 'CPA', value: `$${Math.round(aggregatedSummary.cpa).toLocaleString()}` },
                { label: 'Avg QS', value: `${Math.round(aggregatedSummary.qualityScoreAvg)}/10` },
              ].map((m) => (
                <div key={m.label} className="ed-card p-4">
                  <div className="text-[0.65rem] text-[var(--ed-t3)] mb-1 font-medium uppercase tracking-wider">{m.label}</div>
                  <div className={`text-2xl font-display font-light tracking-tight ${m.color || 'text-foreground'}`}>{m.value}</div>
                </div>
              ))}
            </div>
          )}
          <div className="pt-10">
            <LandingPageDiagnosis campaigns={combinedSemData.campaigns} />
          </div>
          <div className="pt-8">
            <FunnelTeaser />
          </div>
        </div>
      )}

      {activeTab === 'campaigns' && (
        <RestaurantCampaignCards
          restaurants={restaurants}
          onViewLandingPage={(restaurantName?: string) => {
            setAutoOpenRestaurant(restaurantName || null);
            setActiveTab('landing-pages');
          }}
        />
      )}

      {activeTab === 'trends' && isLive && (
        <TrendsTab dailySnapshots={dailySnapshots} restaurants={restaurants} />
      )}
      {activeTab === 'trends' && !isLive && (
        <div className="ed-card p-8 text-center">
          <p className="text-[var(--ed-t2)]">Trends require live Supabase data with daily snapshots.</p>
          <p className="text-sm text-[var(--ed-t4)] mt-2">Connect Supabase and run the initial backfill to enable time-series charts.</p>
        </div>
      )}

      {activeTab === 'full-funnel' && (
        <FullFunnelTab />
      )}

      {activeTab === 'landing-pages' && lpReport && (
        <div>
          <RestaurantPillTabs
            restaurants={restaurants}
            lpReport={lpReport}
            selectedId={selectedRestaurantId || restaurants[0]?.id || ''}
            onSelect={(id) => {
              setSelectedRestaurantId(id);
              setAutoOpenRestaurant(null);
            }}
          />
          {(() => {
            const selected = restaurants.find(r => r.id === (selectedRestaurantId || restaurants[0]?.id));
            if (!selected) return null;
            return (
              <LandingPageStory
                report={lpReport}
                restaurant={selected}
                restaurants={restaurants}
              />
            );
          })()}
        </div>
      )}

      {activeTab === 'landing-pages' && !lpReport && (
        <div className="ed-card p-8 text-center">
          <p className="text-[var(--ed-t2)]">Landing page analysis not yet available.</p>
        </div>
      )}

      {activeTab === 'compare' && (
        <AdGroupComparison semData={combinedSemData} lpReport={lpReport} />
      )}
    </div>
  );
}
